import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, CanDeactivate } from '@angular/router';
import { SessionService } from '../services/session.service';
import { GeolocationService, LocationService } from '../services/location.service';
import { catchError, finalize, firstValueFrom, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { ToastService } from 'src/app/shared/components/toast/toast.service';
import { BackendService } from '../services/backend.service';
import { LanguageService } from '../services/language.service';
import { environment } from 'src/environments/environment';

@Injectable({providedIn: 'root'})
export class BaseGeolocationGuard {
  constructor(private geolocationService: GeolocationService, protected router: Router) {}

  protected async checkGrantedPermission(): Promise<boolean> {
    return await this.geolocationService.grantedPermission();
  }

  // protected async canPromptPermission(): Promise<boolean> {
  //   return await this.geolocationService.canPrompt();
  // }

  protected redirectToPermissionPage(redirectUrl : string, originalState : any) {
    this.router.navigate(['/geolocation/permission'], { state: { 'redirectUrl' : redirectUrl, 'originalState' : originalState }, replaceUrl : true });
  }
}

@Injectable({ providedIn: 'root' })
export class AuthAndLocationGuard
  extends BaseGeolocationGuard
  implements CanActivate
{
  constructor(
    geolocationService: GeolocationService,
    router: Router,
    private session: SessionService,
    private location: LocationService,
    private backend: BackendService,
    private toast: ToastService,
    private languageService: LanguageService,
  ) {
    super(geolocationService, router);
  }

  get isLeft() {
    return this.languageService.CurrentLang.IsLeft;
  }

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean> {
    if (this.session.token) {
      // if(environment.checkGeoLocation){
      const navigation = this.router.getCurrentNavigation();
      let RouteState = navigation?.extras.state;
      let source = RouteState?.['source'] || history.state?.['source'];
      if (source == 'mobile' || sessionStorage.getItem('mobile')) return true;
      const hasPermission = await this.checkGrantedPermission();
      if (!hasPermission && environment.checkGeoLocation) {
        this.redirectToPermissionPage(state.url, RouteState);
        return false;
      } else if (hasPermission && ['apply', 'resume'].includes(source)) {
        const country = await this.getUserCountry();
        //console.log(country)
        if (country == undefined) {
          if (environment.checkGeoLocation) {
            this.redirectToPermissionPage(state.url, RouteState);
            this.toast.showError(
              this.isLeft ? 'ERROR | Unable to retrieve current location' : 'خطأ | لايمكن تحديد موقعك الحالي',
            );
            return false;
          }
          return true;
        } else if (country !== 'saudi')
          this.router.navigate(['/geolocation/outside-ksa']);

        return country === 'saudi';
      }
      // }
      return true;
    } else {
      this.router.navigate(['civil'], {
        queryParams: { returnUrl: state.url },
      });
      return false;
    }
  }

  // private async getUserCountry(): Promise<string|undefined> {
  //   try {
  //     const coords : any = await firstValueFrom(this.location.getUserLocation());
  //     const response = await this.backend.getCountry(coords);
  //     return response?.Data?.CountryName;
  //   } catch(e) {
  //     return undefined;
  //   }
  // }

  private countryData$: Observable<string | undefined> | null = null;
  private async getUserCountry(): Promise<string | undefined> {
    if (!this.countryData$) {
      this.countryData$ = this.location.getUserLocation().pipe(
        switchMap((coords: any) => this.backend.getCountry(coords)),
        map((response) => response?.Data?.CountryName),
        shareReplay(1),
        finalize(() => {
          setTimeout(() => {
            this.countryData$ = null;
          }, 60000);
        }),
        catchError(() => of(undefined)),
      );
    }
    return firstValueFrom(this.countryData$);
  }
}

export interface CanComponentDeactivate {
  canDeactivate: () => Observable<boolean> | Promise<boolean> | boolean;
}

@Injectable({ providedIn: 'root' })
export class SessionTerminationGuard  extends BaseGeolocationGuard implements CanDeactivate<CanComponentDeactivate>{
  constructor(geolocationService: GeolocationService, router: Router, private session: SessionService, private languageService : LanguageService) {
    super(geolocationService, router);
  }

  async canDeactivate(): Promise<boolean> {
    const hasPermission = await this.checkGrantedPermission();
    if(!hasPermission){
      const confirmAction = confirm(this.languageService.CurrentLang.IsLeft ? "Are you sure you want to log out?" : "هل أنت متأكد أنك تريد تسجيل الخروج؟");
      if(confirmAction){
        this.session.logout();
        return true; 
      }
      return false;
    }
    return true;
  }
}